<template>
  <m-stack gap="spacing-2">
    <m-text variant="body-md">{{ $t('lists.hoursOfOperationList.labels.operatingHours') }}</m-text>
    <template v-for="day in daysOfOperation">
      <m-group
        :key="day"
        v-if="Array.isArray(schedule[day]) && schedule[day].length > 0"
        gap="none">
        <div class="day-name">
          <m-text variant="body-md-bold">{{ getScheduleShortDay(day) }}</m-text>
        </div>
        <m-stack gap="none">
          <m-text
            v-for="(range, index) in schedule[day]"
            :key="`${day}-range-${index}`"
            variant="body-md">
            {{
              $t('lists.hoursOfOperationList.messages.scheduleRange', {
                start: formatScheduleClock(range.start),
                end: formatScheduleClock(range.end)
              })
            }}
          </m-text>
        </m-stack>
      </m-group>
    </template>
    <m-text variant="body-md" v-if="noScheduleSet">
      {{ $t('lists.hoursOfOperationList.messages.noScheduleSet') }}
    </m-text>

    <div class="m-t-4" v-if="closedDates.length > 0">
      <m-text variant="body-md">{{ $t('lists.hoursOfOperationList.labels.closedDates') }}</m-text>
    </div>

    <m-stack gap="spacing-2" v-if="closedDates.length > 0">
      <template v-for="(date, index) in closedDates" :key="`closed-date-${index}`">
        <m-text variant="body-md">{{ date }}</m-text>
      </template>
    </m-stack>
  </m-stack>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import { DateTime, Info } from 'luxon';
import { isString } from 'class-validator';
import { isEmpty } from 'lodash';
import { formatScheduleClock } from '@nova/core';
import { useLoggedInCompanyDisplayFormat } from '@/modules/company/composables/useCompanyDisplayFormat';

/**
 * @displayName Hours of Operation List
 */
export default {
  name: 'HoursOfOperationList',
  setup() {
    return {
      companyDisplayFormat: useLoggedInCompanyDisplayFormat()
    };
  },
  props: {
    /**
     * Schedule from API - works with any standardized schedule - Warehouse, Dock, Load Type.
     * See schedule.ts in core lib for more info
     */
    schedule: {
      type: Object,
      required: true
    },
    /**
     * Timezone of warehouse entity belongs to
     */
    timezone: {
      type: String,
      required: true
    }
  },
  computed: {
    noScheduleSet() {
      const scheduleClone = cloneDeep(this.schedule);
      return Object.entries(scheduleClone).every(([key, day]) => {
        if (['version', 'closedIntervals'].includes(key)) {
          return true;
        }
        return !Array.isArray(day) || day.length === 0;
      });
    },
    closedDates() {
      return (this.schedule?.closedIntervals ?? [])
        .sort((a, b) => new Date(a.start) - new Date(b.start))
        .filter(interval => {
          const start = DateTime.fromISO(interval.start, { zone: this.timezone }).startOf('day');
          if (!start.isValid) {
            return false;
          }
          const currentDate = DateTime.now().setZone(this.timezone).startOf('day');
          return start >= currentDate;
        })
        .map(interval =>
          this.companyDisplayFormat.formatCompanyDate(interval.start, this.timezone)
        );
    },
    weekdays() {
      return Info.weekdays('long', { locale: 'en-US' });
    },
    daysOfOperation() {
      const sortedWeekdays = cloneDeep(this.weekdays).map(weekday => weekday.toLowerCase());
      // Because weekdays is mon - sun, we want sunday moved to the start of the array
      sortedWeekdays.unshift(sortedWeekdays[sortedWeekdays.length - 1]);
      sortedWeekdays.pop();

      const weekdays = Object.keys(this.schedule).filter(day => {
        return day !== 'version' && day !== 'closedIntervals';
      });

      const sorted = weekdays.sort((a, b) => {
        const day1 = a.toLowerCase();
        const day2 = b.toLowerCase();
        return sortedWeekdays.indexOf(day1) - sortedWeekdays.indexOf(day2);
      });

      return sorted;
    }
  },
  methods: {
    formatScheduleClock(time) {
      if (!isString(time) || isEmpty(time)) {
        return '';
      }
      return formatScheduleClock(
        time,
        this.companyDisplayFormat.isMilitaryTimeEnabled(),
        this.$i18n.locale
      );
    },
    getScheduleShortDay(day) {
      return this.$t(`global.weekdaysAbbr.${day.substring(0, 3)}`);
    }
  }
};
</script>

<style scoped lang="scss">
.day-name {
  width: 40px;
}
</style>
